'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import { formatDateString } from '@/lib/date'
import type { TaskMeta } from '@/entries'
import { useDocumentView } from '@/document'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LS_LAST_ACTIVE = 'last-active-at'
const SS_SHOWN = 'welcome-back-shown'
const ABSENCE_THRESHOLD_MS = 72 * 60 * 60 * 1000 // 72 hours
const LONG_ABSENCE_DAYS = 30

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function readLS(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLS(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore quota errors
  }
}

function readSS(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSS(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WelcomeBackProps {
  onVisibilityChange?: (visible: boolean) => void
}

interface PriorityTask {
  id: string
  title: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WelcomeBack({ onVisibilityChange }: WelcomeBackProps) {
  const doc = useDocumentView()
  const [dismissed, setDismissed] = useState(false)
  const [lastActiveAt, setLastActiveAt] = useState<string | null>(null)
  const [shouldShow, setShouldShow] = useState(false)

  // On mount: read previous last-active-at, then update it
  useEffect(() => {
    const alreadyShown = readSS(SS_SHOWN) === 'true'
    if (alreadyShown) {
      setShouldShow(false)
      return
    }

    const previous = readLS(LS_LAST_ACTIVE)
    const now = new Date().toISOString()
    writeLS(LS_LAST_ACTIVE, now)

    if (!previous) {
      // First visit ever — don't show welcome back
      setShouldShow(false)
      return
    }

    const elapsed = Date.now() - new Date(previous).getTime()
    if (elapsed >= ABSENCE_THRESHOLD_MS) {
      setLastActiveAt(previous)
      setShouldShow(true)
    } else {
      setShouldShow(false)
    }
  }, [])

  // Notify parent of visibility changes
  const visible = shouldShow && !dismissed
  useEffect(() => {
    onVisibilityChange?.(visible)
  }, [visible, onVisibilityChange])

  // Compute absence days
  const absenceDays = useMemo(() => {
    if (!lastActiveAt) return 0
    // eslint-disable-next-line react-hooks/purity -- Date.now() is stable enough here; value only changes on mount
    const ms = Date.now() - new Date(lastActiveAt).getTime()
    return Math.floor(ms / (24 * 60 * 60 * 1000))
  }, [lastActiveAt])

  // Compute summary data from doc
  const { openTaskCount, newEntryCount, priorityTasks } = useMemo(() => {
    if (!doc || !lastActiveAt) {
      return {
        openTaskCount: 0,
        newEntryCount: 0,
        priorityTasks: [] as PriorityTask[],
      }
    }

    const entries = Object.values(doc.entries)
    let openTasks = 0
    let newEntries = 0
    const candidateTasks: Array<{
      id: string
      title: string
      createdAt: string
      dueDate?: string
      priority?: string
    }> = []

    const lastActiveDate = new Date(lastActiveAt)
    const todayStr = formatDateString(new Date())

    for (const entry of entries) {
      // Count new entries since last active
      if (new Date(entry.createdAt) >= lastActiveDate) {
        newEntries++
      }

      // Count open tasks
      if (entry.type === 'task' && !entry.archivedAt) {
        const meta = entry.metadata as TaskMeta
        if (meta.status !== 'done') {
          openTasks++
          candidateTasks.push({
            id: entry.id,
            title: entry.title,
            createdAt: entry.createdAt,
            dueDate: meta.dueDate,
            priority: meta.priority,
          })
        }
      }
    }

    // Sort priority tasks: due today first, then important, then most recent
    candidateTasks.sort((a, b) => {
      // Due today first
      const aDueToday = a.dueDate === todayStr ? 1 : 0
      const bDueToday = b.dueDate === todayStr ? 1 : 0
      if (aDueToday !== bDueToday) return bDueToday - aDueToday

      // Important (high priority) next
      const aImportant = a.priority === 'high' ? 1 : 0
      const bImportant = b.priority === 'high' ? 1 : 0
      if (aImportant !== bImportant) return bImportant - aImportant

      // Most recently created
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    const topTasks: PriorityTask[] = candidateTasks
      .slice(0, 3)
      .map((t) => ({ id: t.id, title: t.title }))

    return {
      openTaskCount: openTasks,
      newEntryCount: newEntries,
      priorityTasks: topTasks,
    }
  }, [doc, lastActiveAt])

  const handleDismiss = useCallback(() => {
    writeSS(SS_SHOWN, 'true')
    setDismissed(true)
  }, [])

  if (!visible) return null

  return (
    <div className="border-border/30 mb-6 border-b pb-6">
      <h2 className="text-foreground font-serif text-xl">
        {absenceDays > LONG_ABSENCE_DAYS
          ? "It's been a while."
          : 'Welcome back.'}
      </h2>

      <div className="mt-3 space-y-1">
        {openTaskCount > 0 && (
          <p className="text-muted-foreground text-sm">
            {'\u00B7'} {openTaskCount}{' '}
            {openTaskCount === 1 ? 'task is' : 'tasks are'} still waiting
          </p>
        )}
        {newEntryCount > 0 && (
          <p className="text-muted-foreground text-sm">
            {'\u00B7'} {newEntryCount}{' '}
            {newEntryCount === 1 ? 'entry' : 'entries'} captured while you were
            away
          </p>
        )}
      </div>

      {priorityTasks.length > 0 && (
        <>
          <p className="text-foreground mt-4 text-sm font-medium">
            Here&apos;s what matters today:
          </p>
          <div className="mt-2 space-y-1">
            {priorityTasks.map((task) => (
              <p key={task.id} className="text-muted-foreground text-sm">
                {'\u00B7'} {task.title}
              </p>
            ))}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={handleDismiss}
        className="text-muted-foreground hover:text-foreground mt-4 text-sm underline transition-colors"
      >
        Show everything
      </button>
    </div>
  )
}
